import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000';

function authHeader() {
  const auth = JSON.parse(localStorage.getItem('zocs_auth') || '{}');
  return { Authorization: `Bearer ${auth.token}` };
}

// ---- Feature flags ----
export async function getFeatureFlags() {
  const res = await axios.get(`${API_BASE}/admin/skipzy/feature-flags`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function createFeatureFlag(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/feature-flags`, body, {
    headers: authHeader(),
  });
  return res.data;
}

export async function updateFeatureFlag(key, patch) {
  const res = await axios.patch(`${API_BASE}/admin/skipzy/feature-flags/${key}`, patch, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Brands ----
export async function getBrands() {
  const res = await axios.get(`${API_BASE}/admin/skipzy/brands`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function addBrand(name) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/brands`, { name }, {
    headers: authHeader(),
  });
  return res.data;
}

// Returns the remaining brands.
export async function removeBrand(id) {
  const res = await axios.delete(`${API_BASE}/admin/skipzy/brands/${id}`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function reorderBrands(ids) {
  const res = await axios.post(
    `${API_BASE}/admin/skipzy/brands/reorder`,
    { ids },
    { headers: authHeader() }
  );
  return res.data;
}

// ---- Coupons ----
export async function getCoupons() {
  const res = await axios.get(`${API_BASE}/admin/skipzy/coupons`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function updateCoupon(code, patch) {
  const res = await axios.patch(`${API_BASE}/admin/skipzy/coupons/${code}`, patch, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Coin coupons ----
export async function getCoinCoupons() {
  const res = await axios.get(`${API_BASE}/admin/skipzy/coin-coupons`, {
    headers: authHeader(),
  });
  return res.data;
}

// body: { code, coins, expiry_days, valid_until?, max_redemptions?, notes? }
export async function createCoinCoupon(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/coin-coupons`, body, {
    headers: authHeader(),
  });
  return res.data;
}

export async function updateCoinCoupon(code, patch) {
  const res = await axios.patch(`${API_BASE}/admin/skipzy/coin-coupons/${code}`, patch, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Notify ----
// body: { phones: [...], message, coins?, expiry_days? } — expiry_days is
// required exactly when coins is set. Returns { results, notified_count }.
export async function notifyPhones(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/notify`, body, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Coins deduct ----
// q: an order number, appointment id or 10-digit phone.
export async function lookupCoins(q) {
  const res = await axios.get(`${API_BASE}/admin/skipzy/coins/lookup`, {
    headers: authHeader(),
    params: { q },
  });
  return res.data;
}

// body: { reason } — no amount: it always takes exactly what the order earned.
// performed_by is filled in server-side.
export async function deductCoinsForOrder(orderId, body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/coins/orders/${orderId}/deduct`, body, {
    headers: authHeader(),
  });
  return res.data;
}

// body: { phone, coins, reason }
export async function deductCoinsForPhone(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/coins/deduct`, body, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Reward claims ----
// status omitted lists every claim; pass 'pending' for the review queue.
export async function getRewardClaims(status) {
  const res = await axios.get(`${API_BASE}/admin/skipzy/reward-claims`, {
    headers: authHeader(),
    params: status ? { status } : undefined,
  });
  return res.data;
}

// body: { approved, reject_reason? } — reviewed_by is filled in server-side.
export async function decideRewardClaim(id, body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/reward-claims/${id}/decide`, body, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Home ads ----
export async function getHomeAds() {
  const res = await axios.get(`${API_BASE}/admin/skipzy/home-ads`, {
    headers: authHeader(),
  });
  return res.data;
}

export async function createHomeAd(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/home-ads`, body, {
    headers: authHeader(),
  });
  return res.data;
}

export async function updateHomeAd(slug, patch) {
  const res = await axios.patch(`${API_BASE}/admin/skipzy/home-ads/${slug}`, patch, {
    headers: authHeader(),
  });
  return res.data;
}

export async function reorderHomeAds(slugs) {
  const res = await axios.post(
    `${API_BASE}/admin/skipzy/home-ads/reorder`,
    { slugs },
    { headers: authHeader() }
  );
  return res.data;
}

// Uploads the creative and returns its CloudFront URL — that URL is what a
// later create/update stores on the ad. Note the headers: only the auth one, so
// axios sets the multipart Content-Type with its own boundary. Setting it by
// hand here silently breaks the upload.
export async function uploadHomeAdImage(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(`${API_BASE}/admin/skipzy/home-ads/media`, form, {
    headers: authHeader(),
  });
  return res.data.url;
}

// ZOCS sync — ZOCS appointments vs the customer app, preview, and sync.
export async function listZocsAppointments(params) {
  const res = await axios.get(`${API_BASE}/admin/skipzy/zocs-appointments`, {
    params, headers: authHeader(),
  });
  return res.data;
}

export async function previewZocsSync(code, phone_10, scheduled_date) {
  const res = await axios.get(
    `${API_BASE}/admin/skipzy/zocs-appointments/${encodeURIComponent(code)}/preview`,
    { params: { phone_10, scheduled_date }, headers: authHeader() }
  );
  return res.data;
}

export async function syncZocsAppointment(code, phone_10, scheduled_date) {
  const res = await axios.post(
    `${API_BASE}/admin/skipzy/zocs-appointments/${encodeURIComponent(code)}/sync`,
    { phone_10, scheduled_date },
    { headers: authHeader() }
  );
  return res.data;
}

// ---- Slots (Order Ops → Slots Manage) ----
export async function getSlots(dateFrom, dateTo) {
  const res = await axios.get(`${API_BASE}/admin/skipzy/slots`, {
    headers: authHeader(),
    params: { date_from: dateFrom, date_to: dateTo },
  });
  return res.data;
}

export async function updateSlot(slotId, patch) {
  const res = await axios.patch(`${API_BASE}/admin/skipzy/slots/${slotId}`, patch, {
    headers: authHeader(),
  });
  return res.data;
}

export async function bulkUpdateSlots(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/slots/bulk`, body, {
    headers: authHeader(),
  });
  return res.data;
}

// ---- Bulk coin gift (CSV upload) ----
// body: { rows: [{ phone, coins }], expiry_days, message }. Returns
// { results: [{ phone, coins, notified, reason }], notified_count, skipped_count }.
// A phone with no app account comes back notified:false — it is not created.
export async function bulkGiftCoins(body) {
  const res = await axios.post(`${API_BASE}/admin/skipzy/coins/bulk-gift`, body, {
    headers: authHeader(),
    timeout: 120000,
  });
  return res.data;
}
